/**
 * Review list hook + helpers.
 *
 * Reviews are keyed by (kb_number, geography). The hook returns both the flat
 * list and a denormalised ``byKb`` Map so callers can render per-form badges
 * without re-grouping each render.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ApiError,
  createReview as apiCreateReview,
  deleteReview as apiDeleteReview,
  listReviews as apiListReviews,
} from '../api';
import type { Review, ReviewCreateInput, ReviewStatus } from '../types';

import type { ReviewFilters } from '../api';

export type ReviewMap = Map<string, Map<string, Review>>;

/** Look up status for a (kb, geo) pair; absent rows are implicitly pending. */
export function getReviewStatus(
  byKb: ReviewMap,
  kbNumber: string,
  geography: string,
): ReviewStatus {
  return byKb.get(kbNumber)?.get(geography)?.status ?? 'pending_review';
}

export function useReviews(filters: ReviewFilters): {
  reviews: Review[];
  byKb: ReviewMap;
  error: string | null;
  loading: boolean;
  refresh: () => void;
} {
  const key = useMemo(() => JSON.stringify(filters), [filters]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiListReviews(filters)
      .then((r) => {
        if (!cancelled) {
          setReviews(r);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(describe(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);

  const byKb = useMemo<ReviewMap>(() => {
    const m: ReviewMap = new Map();
    for (const r of reviews) {
      const inner = m.get(r.kb_number) ?? new Map<string, Review>();
      inner.set(r.geography, r);
      m.set(r.kb_number, inner);
    }
    return m;
  }, [reviews]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  return { reviews, byKb, error, loading, refresh };
}

export async function createReview(payload: ReviewCreateInput) {
  return apiCreateReview(payload);
}

export async function deleteReview(kbNumber: string, geography: string) {
  return apiDeleteReview(kbNumber, geography);
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = e.detail;
    if (detail && typeof detail === 'object' && 'detail' in detail) {
      const inner = (detail as { detail: unknown }).detail;
      if (typeof inner === 'string') return inner;
    }
    return `${e.status}: ${e.message}`;
  }
  return e instanceof Error ? e.message : String(e);
}
